/**
 * Barra da página: as abas (`HeaderNavigations`) e as ações (`PageActions`)
 * que as telas punham no header do Metronic aparecem aqui, no topo do
 * conteúdo, sem mudar nada nas telas. Fica presa logo abaixo do header ao
 * rolar, como ficavam no header fixo.
 *
 * Tela Ops-Next só põe aqui as páginas irmãs da seção: a ação principal dela
 * fica no fim da barra da lista (o `action` do `Toolbar`), e formulário de
 * criar ou de editar não é aba (regra 7 de `app/ops-next/architecture.test.ts`
 * e o teste do roteiro). As `PageActions` que sobram são de telas Metronic.
 *
 * Quando as abas não cabem, elas rolam na horizontal: as setas das pontas
 * levam às escondidas, e a aba atual vem à vista ao abrir a página. As setas
 * são só para o mouse e o toque (fora do Tab e do leitor de tela): o teclado
 * chega a toda aba com o Tab, e a barra traz a focada à vista, fora da seta e
 * do esmaecimento (o navegador sozinho não rola a aba que já aparece em parte).
 *
 * Publica `--ops-chrome-h` (header + esta barra) no wrapper: as telas que
 * calculam altura pela janela descontam por ela, e não mais pelos 64px do
 * header antigo.
 */
import {
  FC,
  MouseEvent,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {Link, useLocation} from 'react-router-dom';
import clsx from 'clsx';

import {PageLink, usePageData} from '../../core';
import {isTabActive} from './breadcrumbTrail';

export const APP_HEADER_HEIGHT = 48;

const WRAPPER_ID = 'kt_wrapper';

/** Quanto da largura visível a seta rola de cada vez. */
const SCROLL_STEP = 0.8;
/**
 * Folga que a aba atual guarda da ponta da `nav` quando é trazida à vista:
 * a seta (28px) mais o esmaecimento (24px), em `appHeader.scss`.
 */
const REVEAL_GUTTER = 52;

type Overflow = {start: boolean; end: boolean};

const NO_OVERFLOW: Overflow = {start: false, end: false};

/** Há abas escondidas antes (`start`) e/ou depois (`end`) do que se vê? */
const measureOverflow = (nav: HTMLElement): Overflow => ({
  start: nav.scrollLeft > 1,
  end: nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1,
});

/**
 * Traz a aba para dentro da `nav`, a `REVEAL_GUTTER` das pontas, só ajustando
 * o `scrollLeft` dela: `scrollIntoView` rolaria a página inteira junto.
 */
const revealTab = (nav: HTMLElement, tab: HTMLElement) => {
  const left = tab.offsetLeft;
  const right = left + tab.offsetWidth;
  if (left - REVEAL_GUTTER < nav.scrollLeft) {
    nav.scrollLeft = Math.max(0, left - REVEAL_GUTTER);
  } else if (right + REVEAL_GUTTER > nav.scrollLeft + nav.clientWidth) {
    nav.scrollLeft = right + REVEAL_GUTTER - nav.clientWidth;
  }
};

const revealActiveTab = (nav: HTMLElement) => {
  const active = nav.querySelector<HTMLElement>('.is-active');
  if (active) revealTab(nav, active);
};

/**
 * O foco veio do teclado? No clique, rolar a aba no `mousedown` a tiraria de
 * baixo do ponteiro antes do `click`; a página nova traz a aba atual à vista
 * sozinha. Sem `:focus-visible` (o jsdom), conta como teclado.
 */
const focusFromKeyboard = (element: HTMLElement) => {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
};

/**
 * A seta fica fora do Tab e do leitor de tela (`aria-hidden`): o clique nela
 * não leva o foco, que fica onde estava (e não cai no `body` quando a seta
 * some no fim da rolagem).
 */
const keepFocus = (event: MouseEvent) => event.preventDefault();

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** `PageActions` deixa `[]` ou `''` quando sai: isso não é ação. */
export const hasPageActions = (actions: ReactNode) =>
  actions !== null &&
  actions !== undefined &&
  actions !== false &&
  actions !== '' &&
  !(Array.isArray(actions) && actions.length === 0);

/**
 * Abas que valem a barra. Uma aba só, que é a própria página, não leva a
 * lugar nenhum (o breadcrumb já diz onde a pessoa está): fica de fora.
 */
export const pageBarTabs = (tabs: PageLink[] = [], pathname: string) => {
  const real = tabs.filter(({isSeparator, path}) => !isSeparator && path);
  if (
    real.length === 1 &&
    isTabActive(pathname, real[0].path, real[0].alsoActiveOn)
  ) {
    return [];
  }
  return real;
};

const PageBar: FC = () => {
  const {pageHeaderNavigation, pageActions} = usePageData();
  const {pathname} = useLocation();
  const barRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [overflow, setOverflow] = useState<Overflow>(NO_OVERFLOW);

  const tabs = pageBarTabs(pageHeaderNavigation, pathname);
  // Os efeitos refazem quando as abas mudam, não só quando muda o número
  // delas: duas seções com quatro abas cada são abas diferentes
  const tabsKey = tabs.map(({path}) => path).join('|');
  const actions = hasPageActions(pageActions) ? pageActions : null;
  const show = tabs.length > 0 || Boolean(actions);

  useLayoutEffect(() => {
    // Pelo id: na montagem, o ref do wrapper (o pai) ainda não está ligado
    const wrapper = document.getElementById(WRAPPER_ID);
    if (!wrapper) return undefined;
    const publish = () => {
      const bar = barRef.current?.offsetHeight || 0;
      wrapper.style.setProperty(
        '--ops-chrome-h',
        `${APP_HEADER_HEIGHT + bar}px`
      );
    };
    publish();
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [show, tabsKey]);

  const updateOverflow = useCallback(() => {
    const nav = navRef.current;
    const next = nav ? measureOverflow(nav) : NO_OVERFLOW;
    setOverflow((current) =>
      current.start === next.start && current.end === next.end ? current : next
    );
  }, []);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      updateOverflow();
      return undefined;
    }
    revealActiveTab(nav);
    updateOverflow();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // A `nav` muda de largura com a janela e o menu lateral; as abas, com a
    // fonte que acaba de carregar. A cada mudança a aba atual volta à vista:
    // o aviso só vem com mudança de tamanho, nunca com a rolagem das setas
    const observer = new ResizeObserver(() => {
      revealActiveTab(nav);
      updateOverflow();
    });
    observer.observe(nav);
    Array.from(nav.children).forEach((tab) => observer.observe(tab));
    return () => observer.disconnect();
  }, [pathname, tabsKey, updateOverflow]);

  const scrollTabs = (direction: -1 | 1) => {
    const nav = navRef.current;
    if (!nav) return;
    nav.scrollBy({
      left: direction * Math.round(nav.clientWidth * SCROLL_STEP),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  if (!show) return null;

  return (
    <div ref={barRef} className='ops-pagebar'>
      {tabs.length > 0 && (
        <div className='ops-pagebar__tabwrap'>
          <nav
            ref={navRef}
            className={clsx(
              'ops-pagebar__tabs',
              overflow.start && 'has-overflow-start',
              overflow.end && 'has-overflow-end'
            )}
            aria-label='Páginas da seção'
            onScroll={updateOverflow}
            onFocus={(event) => {
              const nav = navRef.current;
              const tab = event.target;
              if (!nav || tab === nav || !focusFromKeyboard(tab)) return;
              revealTab(nav, tab);
              updateOverflow();
            }}
          >
            {tabs.map(({title, path, fontIcon, alsoActiveOn}) => {
              const active = isTabActive(pathname, path, alsoActiveOn);
              return (
                <Link
                  key={path}
                  to={path}
                  className={clsx('ops-pagebar__tab', active && 'is-active')}
                  aria-current={active ? 'page' : undefined}
                >
                  {fontIcon && (
                    <i className={clsx('bi', fontIcon)} aria-hidden='true' />
                  )}
                  {title.trim()}
                </Link>
              );
            })}
          </nav>
          {overflow.start && (
            <button
              type='button'
              tabIndex={-1}
              aria-hidden='true'
              className='ops-pagebar__scroll ops-pagebar__scroll--start'
              onMouseDown={keepFocus}
              onClick={() => scrollTabs(-1)}
            >
              <i className='bi bi-chevron-left' />
            </button>
          )}
          {overflow.end && (
            <button
              type='button'
              tabIndex={-1}
              aria-hidden='true'
              className='ops-pagebar__scroll ops-pagebar__scroll--end'
              onMouseDown={keepFocus}
              onClick={() => scrollTabs(1)}
            >
              <i className='bi bi-chevron-right' />
            </button>
          )}
        </div>
      )}
      {actions && <div className='ops-pagebar__actions'>{actions}</div>}
    </div>
  );
};

export default PageBar;
